import React, { useState, useEffect } from 'react';
import StepIcon from './StepIcon';

const LoadingStepper = ({ phase, isPreparingUpload, isExtracting }) => {
  const activePhase = isPreparingUpload ? 'upload' : isExtracting ? 'extract' : phase;
  const [stepProgress, setStepProgress] = useState(0);

  useEffect(() => {
    const startTime = Date.now();
    setStepProgress(0);

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      let progress = 0;

      if (elapsed <= 4000) {
        // 0s to 4s: climb smoothly to 45%
        progress = (elapsed / 4000) * 45;
      } else if (elapsed <= 6000) {
        // 4s to 6s: pause at 45%
        progress = 45;
      } else if (elapsed <= 9000) {
        // 6s to 9s: climb from 45% to 85%
        progress = 45 + ((elapsed - 6000) / 3000) * (85 - 45);
      } else if (elapsed <= 11000) {
        // 9s to 11s: pause at 85%
        progress = 85;
      } else if (elapsed <= 12000) {
        // 11s to 12s: climb from 85% to 100%
        progress = 85 + ((elapsed - 11000) / 1000) * (100 - 85);
      } else {
        progress = 100;
        clearInterval(interval);
      }

      setStepProgress(progress);
    }, 50); // 50ms ticks for ultra-smooth rendering

    return () => clearInterval(interval);
  }, [activePhase]);

  if (activePhase === 'extract') {
    return (
      <div className="process-container" role="status" aria-live="polite">
        <div className="process-card active">
          <StepIcon stepKey="extract" status="active" progress={45} />
          <div className="process-card-content">
            <h4 className="process-card-title">Reading the job details</h4>
            <p className="process-card-subtitle">Getting details from the URL...</p>
          </div>
        </div>
      </div>
    );
  }

  const steps = [
    {
      key: 'upload',
      title: 'Receiving your resume',
      subtitle: 'Saving it securely...',
      stepKey: 'upload',
    },
    {
      key: 'parse',
      title: 'Reviewing your background',
      subtitle: 'Learning about your work history...',
      stepKey: 'parse',
    },
    {
      key: 'analyze',
      title: 'Matching qualifications',
      subtitle: 'Finding the best ways to highlight your fit...',
      stepKey: 'analyze',
    },
    {
      key: 'complete',
      title: 'Almost ready!',
      subtitle: 'Preparing your customized dashboard...',
      stepKey: 'complete',
    }
  ];

  const stageOrder = ['upload', 'parse', 'analyze', 'complete'];
  const activeIdx = stageOrder.indexOf(activePhase);

  return (
    <div className="process-container" role="status" aria-live="polite">
      {/* Upper Area: The Stack */}
      <div className="process-stack-container">
        {steps.map((step, idx) => {
          const isCompleted = idx < activeIdx;
          const isCurrent = idx === activeIdx;
          if (isCompleted) return null;

          const pendingIdx = idx - activeIdx;
          const offsetStyles = {
            transform: `translateY(${pendingIdx * -8}px) scale(${1 - pendingIdx * 0.04})`,
            opacity: pendingIdx === 0 ? 1 : pendingIdx === 1 ? 0.6 : 0.3,
            zIndex: 10 - pendingIdx,
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            transition: 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1), z-index 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
          };

          return (
            <div key={step.key} className={`process-card ${isCurrent ? 'active' : 'pending'}`} style={offsetStyles}>
              <StepIcon stepKey={step.stepKey} status={isCurrent ? 'active' : 'pending'} progress={stepProgress} />
              <div className="process-card-content">
                <h4 className="process-card-title">{step.title}</h4>
                {isCurrent && (
                  <p className="process-card-subtitle">
                    {step.subtitle} ({Math.min(100, Math.floor(stepProgress))}%)
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Lower Area: Completed List */}
      <div className="process-completed-list" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {steps.map((step, idx) => {
          const isCompleted = idx < activeIdx;
          if (!isCompleted) return null;

          return (
            <div key={step.key} className="process-card completed animate-slide-down">
              <StepIcon stepKey={step.stepKey} status="completed" />
              <div className="process-card-content">
                <h4 className="process-card-title">{step.title}</h4>
                <p className="process-card-subtitle" style={{ color: 'var(--color-accent-ferrari)', opacity: 0.8 }}>Completed</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LoadingStepper;
